import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { MagnifyingGlassIcon, FunnelIcon, XMarkIcon } from '@heroicons/react/24/outline';
import api from '../services/api';
import Layout from '../components/layout/Layout';
import { StatusBadge, EstadoBadge } from '../components/ui/Badge';
import Spinner from '../components/ui/Spinner';
import EmptyState from '../components/ui/EmptyState';
import ModalStatus, { proximoStatus, statusAnterior } from '../components/ui/ModalStatus';
import { formatCurrency } from '../utils/formatters';
import { useAuth } from '../contexts/AuthContext';

const TAMANHOS = ['Berço', 'Solteiro', 'Solteiro Extra', 'Casal', 'Queen', 'King', 'Personalizado'];
const STATUS   = ['Disponível', 'Reservado', 'Vendido'];
const ESTADOS  = ['Novo', 'Usado', 'Danificado'];

const Catalogo = () => {
  const [produtos, setProdutos]       = useState([]);
  const [categorias, setCategorias]   = useState([]);
  const [marcas, setMarcas]           = useState([]);
  const [loading, setLoading]         = useState(true);
  const [showFilters, setShowFilters] = useState(false);

  const { isAdmin } = useAuth();

  // Estado do modal de status
  const [modal, setModal] = useState({ open: false, produto: null, novoStatus: null });
  const [salvando, setSalvando] = useState(false);

  const [filtros, setFiltros] = useState({
    busca: '', categoria: '', marca: '', tamanho: '', status: '', estado: '',
  });

  const fetchProdutos = useCallback(async () => {
    setLoading(true);
    try {
      const params = Object.fromEntries(
        Object.entries(filtros).filter(([, v]) => v !== '')
      );
      const res = await api.get('/produtos', { params });
      setProdutos(res.data);
    } catch (err) {
      console.error('Erro ao buscar produtos:', err);
    } finally {
      setLoading(false);
    }
  }, [filtros]);

  useEffect(() => { fetchProdutos(); }, [fetchProdutos]);

  useEffect(() => {
    Promise.all([api.get('/categorias'), api.get('/marcas')]).then(([c, m]) => {
      setCategorias(c.data);
      setMarcas(m.data);
    });
  }, []);

  const handleFiltro = (key, value) => setFiltros((prev) => ({ ...prev, [key]: value }));
  const limparFiltros = () =>
    setFiltros({ busca: '', categoria: '', marca: '', tamanho: '', status: '', estado: '' });

  const filtrosAtivos = Object.values(filtros).filter(Boolean).length;

  // Abre modal para avançar ou desfazer status
  const abrirModal = (e, produto, novoStatus) => {
    e.preventDefault();  // impede navegação do Link pai
    e.stopPropagation();
    setModal({ open: true, produto, novoStatus });
  };

  const confirmarStatus = async () => {
    setSalvando(true);
    try {
      await api.patch(`/produtos/${modal.produto.id}/status`, { status: modal.novoStatus });
      // Atualiza localmente sem refetch
      setProdutos((prev) =>
        prev.map((p) => p.id === modal.produto.id ? { ...p, status: modal.novoStatus } : p)
      );
      setModal({ open: false, produto: null, novoStatus: null });
    } catch (err) {
      console.error('Erro ao atualizar status:', err);
    } finally {
      setSalvando(false);
    }
  };

  return (
    <Layout>
      {/* Cabeçalho */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Catálogo de Mostruário</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            {loading ? 'Carregando...' : `${produtos.length} produto(s) encontrado(s)`}
          </p>
        </div>
        <button onClick={() => setShowFilters(!showFilters)} className="btn-secondary relative">
          <FunnelIcon className="w-4 h-4" />
          Filtros
          {filtrosAtivos > 0 && (
            <span className="absolute -top-1.5 -right-1.5 bg-gray-900 text-white text-xs w-4 h-4 rounded-full flex items-center justify-center">
              {filtrosAtivos}
            </span>
          )}
        </button>
      </div>

      {/* Barra de busca */}
      <div className="relative mb-4">
        <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input
          type="text"
          placeholder="Buscar por nome ou código..."
          value={filtros.busca}
          onChange={(e) => handleFiltro('busca', e.target.value)}
          className="input pl-9"
        />
      </div>

      {/* Painel de filtros */}
      {showFilters && (
        <div className="card p-4 mb-6">
          <div className="flex items-center justify-between mb-4">
            <span className="text-sm font-medium text-gray-700">Filtros</span>
            {filtrosAtivos > 0 && (
              <button onClick={limparFiltros} className="text-xs text-red-500 hover:text-red-700 flex items-center gap-1">
                <XMarkIcon className="w-3 h-3" /> Limpar tudo
              </button>
            )}
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            <select className="input text-sm" value={filtros.categoria} onChange={(e) => handleFiltro('categoria', e.target.value)}>
              <option value="">Categoria</option>
              {categorias.map((c) => <option key={c.id} value={c.id}>{c.nome}</option>)}
            </select>
            <select className="input text-sm" value={filtros.marca} onChange={(e) => handleFiltro('marca', e.target.value)}>
              <option value="">Marca</option>
              {marcas.map((m) => <option key={m.id} value={m.id}>{m.nome}</option>)}
            </select>
            <select className="input text-sm" value={filtros.tamanho} onChange={(e) => handleFiltro('tamanho', e.target.value)}>
              <option value="">Tamanho</option>
              {TAMANHOS.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
            <select className="input text-sm" value={filtros.status} onChange={(e) => handleFiltro('status', e.target.value)}>
              <option value="">Status</option>
              {STATUS.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            <select className="input text-sm" value={filtros.estado} onChange={(e) => handleFiltro('estado', e.target.value)}>
              <option value="">Estado</option>
              {ESTADOS.map((e) => <option key={e} value={e}>{e}</option>)}
            </select>
          </div>
        </div>
      )}

      {/* Grid de produtos */}
      {loading ? (
        <div className="flex justify-center py-20"><Spinner size="lg" /></div>
      ) : produtos.length === 0 ? (
        <EmptyState
          title="Nenhum produto encontrado"
          description="Tente ajustar os filtros ou a busca."
          action={filtrosAtivos > 0 && (
            <button onClick={limparFiltros} className="btn-secondary text-sm">Limpar filtros</button>
          )}
        />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {produtos.map((produto) => {
            const proximo  = proximoStatus(produto.status);
            const anterior = statusAnterior(produto.status);

            return (
              <div key={produto.id} className={`card overflow-hidden flex flex-col
                ${produto.status === 'Vendido' ? 'border-red-200 bg-gray-50' : ''}
                ${produto.status === 'Reservado' ? 'border-amber-300' : ''}`}>
                {/* Área clicável → detalhe */}
                <Link to={`/produto/${produto.id}`} className="group flex-1">
                  {/* Foto */}
                  <div className="aspect-[4/3] bg-gray-100 overflow-hidden relative">
                    {produto.foto_capa ? (
                      <img
                        src={`${process.env.REACT_APP_API_URL?.replace('/api', '')}${produto.foto_capa}`}
                        alt={produto.nome}
                        className={`w-full h-full object-cover transition-transform duration-200
                          ${produto.status === 'Vendido'
                            ? 'grayscale opacity-60'
                            : 'group-hover:scale-105'
                          }`}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-gray-300 text-xs">
                        Sem foto
                      </div>
                    )}

                    {/* Tag de desconto no canto superior esquerdo */}
                    {produto.preco_original && produto.preco_mostruario &&
                      parseFloat(produto.preco_original) > parseFloat(produto.preco_mostruario) && (
                      <div className="absolute top-2 left-2 pointer-events-none">
                        <span className="bg-red-600 text-white text-xs font-bold px-2 py-1 rounded-md shadow select-none">
                          -{Math.round((1 - produto.preco_mostruario / produto.preco_original) * 100)}%
                        </span>
                      </div>
                    )}

                    {/* Faixa diagonal VENDIDO */}
                    {produto.status === 'Vendido' && (
                      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                        <span className="
                          rotate-[-35deg] px-6 py-1.5
                          bg-red-600 text-white text-sm font-bold tracking-widest uppercase
                          shadow-lg opacity-90 select-none
                        ">
                          Vendido
                        </span>
                      </div>
                    )}

                    {/* Faixa diagonal RESERVADO */}
                    {produto.status === 'Reservado' && (
                      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                        <span className="
                          rotate-[-35deg] px-6 py-1.5
                          bg-amber-400 text-amber-900 text-sm font-bold tracking-widest uppercase
                          shadow-lg opacity-90 select-none
                        ">
                          Reservado
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Info */}
                  <div className={`p-4 pb-3 ${produto.status === 'Vendido' ? 'opacity-50' : ''}`}>
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <h3 className="text-sm font-semibold text-gray-900 leading-tight line-clamp-2">
                        {produto.nome}
                      </h3>
                      <StatusBadge status={produto.status} />
                    </div>

                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      <span className="text-xs text-gray-500">{produto.categoria_nome}</span>
                      {produto.tamanho && (
                        <>
                          <span className="text-gray-200">·</span>
                          <span className="text-xs text-gray-500">{produto.tamanho}</span>
                        </>
                      )}
                      {produto.estado && <EstadoBadge estado={produto.estado} />}
                    </div>

                    {produto.preco_mostruario && (
                      <div className="mt-1">
                        <p className="text-base font-bold text-gray-900 leading-tight">
                          {formatCurrency(produto.preco_mostruario)}
                        </p>
                        {produto.preco_original &&
                          parseFloat(produto.preco_original) > parseFloat(produto.preco_mostruario) && (
                          <p className="text-xs text-gray-400 line-through">
                            {formatCurrency(produto.preco_original)}
                          </p>
                        )}
                      </div>
                    )}
                    {produto.codigo && (
                      <p className="text-xs text-gray-400 mt-0.5">{produto.codigo}</p>
                    )}
                  </div>
                </Link>

                {/* Botões de status — fora do Link */}
                {(proximo || anterior) && (
                  <div className="px-4 pb-4 pt-1 flex gap-2 border-t border-gray-50 mt-1">
                    {proximo && (
                      <button
                        onClick={(e) => abrirModal(e, produto, proximo)}
                        className={`flex-1 text-xs font-medium py-1.5 rounded-lg transition-colors
                          ${proximo === 'Reservado'
                            ? 'bg-amber-100 text-amber-800 hover:bg-amber-200'
                            : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                          }`}
                      >
                        {proximo === 'Reservado' ? 'Reservar' : 'Marcar como vendido'}
                      </button>
                    )}
                    {/* Desfazer: de Vendido só admin pode; de Reservado qualquer um */}
                    {anterior && (produto.status !== 'Vendido' || isAdmin) && (
                      <button
                        onClick={(e) => abrirModal(e, produto, anterior)}
                        className="flex-1 text-xs font-medium py-1.5 rounded-lg bg-gray-50 text-gray-500 hover:bg-gray-100 transition-colors border border-gray-200"
                      >
                        ↩ Desfazer
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Modal de confirmação */}
      <ModalStatus
        open={modal.open}
        onClose={() => !salvando && setModal({ open: false, produto: null, novoStatus: null })}
        nomeProduto={modal.produto?.nome}
        novoStatus={modal.novoStatus}
        onConfirmar={confirmarStatus}
        loading={salvando}
      />
    </Layout>
  );
};

export default Catalogo;
